import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Space, Tag, Table, theme, Divider } from 'antd';
import { MealDto } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { LocalizedContentShow } from '../../components/LocalizedContentTabs';

export const MealShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<MealDto>({ resource: 'meals' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="meals">
      <div style={{ padding: '8px 0' }}>
        <LocalizedContentShow
          record={item}
          fields={[
            { key: 'name', label: 'Name' },
            { key: 'description', label: 'Description', textarea: true },
          ]}
        />

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
          }}
        >
          <Field label="Restaurant">{item.restaurantName || item.restaurantId}</Field>
          <Field label="Menu Category">{item.menuCategoryName || item.menuCategoryId || '—'}</Field>
          <Field label="Image URL">
            {item.imageUrl ? <a href={item.imageUrl} target="_blank" rel="noreferrer">{item.imageUrl}</a> : '—'}
          </Field>

          <Divider orientation="left" plain>Serving</Divider>
          <Field label="Serving Size">{item.servingSize} {item.servingUnit}</Field>

          <Divider orientation="left" plain>Nutrition per Serving</Divider>
          <Field label="Calories">{item.caloriesPerServing} kcal</Field>
          <Field label="Protein">{item.proteinPerServing} g</Field>
          <Field label="Fat">{item.fatPerServing} g</Field>
          <Field label="Carbs">{item.carbsPerServing} g</Field>
          <Field label="Fiber">{item.fiberPerServing != null ? `${item.fiberPerServing} g` : '—'}</Field>
          <Field label="Sodium">{item.sodiumPerServing != null ? `${item.sodiumPerServing} mg` : '—'}</Field>

          <Divider orientation="left" plain>Other</Divider>
          <Field label="Tags">
            {(item.tags || []).length > 0 ? (
              <Space wrap>
                {item.tags.map((t) => (
                  <Tag key={t} style={{ borderRadius: 9999, padding: '2px 10px' }}>
                    {t}
                  </Tag>
                ))}
              </Space>
            ) : (
              '—'
            )}
          </Field>

          {item.ingredients && item.ingredients.length > 0 && (
            <>
              <Divider orientation="left" plain>Ingredients ({item.ingredients.length})</Divider>
              <Table
                dataSource={item.ingredients}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: 800 }}
              >
                <Table.Column dataIndex="displayName" title="Name" width={180} />
                <Table.Column dataIndex="amount" title="Amount" width={90} />
                <Table.Column dataIndex="amountUnit" title="Unit" width={80} />
                <Table.Column dataIndex="calories" title="Calories" width={90} />
                <Table.Column dataIndex="protein" title="Protein (g)" width={100} />
                <Table.Column dataIndex="fat" title="Fat (g)" width={90} />
                <Table.Column dataIndex="carbs" title="Carbs (g)" width={90} />
                <Table.Column
                  dataIndex="fiber"
                  title="Fiber (g)"
                  width={90}
                  render={(v: number) => v != null ? v : '—'}
                />
                <Table.Column
                  dataIndex="sodium"
                  title="Sodium (mg)"
                  width={100}
                  render={(v: number) => v != null ? v : '—'}
                />
              </Table>
            </>
          )}
        </div>
      </div>
    </Show>
  );
};
